// Presentation-layer capability predicates and server-authoritative subject
// access types over server-provided effective permissions. These decide what
// the UI shows; server authorization remains authoritative and must never be
// bypassed by client state.

export type ZoriCapabilityExplanation = {
  allowed: boolean;
  required: readonly string[];
  missing: readonly string[];
};

export type ZoriSubjectType = "user" | "group" | "agent" | "service" | "integration";

export type ZoriSubjectRef = {
  type: ZoriSubjectType;
  id: string;
  name?: string;
  organizationId: string;
  workspaceId?: string | null;
};

export type ZoriResourceRef = {
  type: string;
  id?: string;
  organizationId?: string;
  workspaceId?: string | null;
};

export type ZoriAccessScope = {
  organizationId: string;
  workspaceId?: string | null;
};

export type ZoriAccessDecision = {
  decisionId: string;
  allowed: boolean;
  subject: ZoriSubjectRef;
  action: string;
  resource: ZoriResourceRef;
  scope: ZoriAccessScope;
  reason: string;
  policy: "rbac" | "agent_capability_policy" | "service_principal_policy" | "integration_principal_policy" | "default_deny" | "explicit_deny";
  denialCode: "none" | "permission_denied" | "subject_not_found" | "subject_out_of_scope" | "invalid_subject" | "resource_not_found" | "resource_out_of_scope" | "invalid_resource" | "revoked_grant" | "stale_policy" | "dependency_unavailable";
  resourceStatus: "verified" | "unverified" | "not_found" | "revoked";
  sources: Array<{
    sourceType: "direct" | "role" | "team" | "agent_capability" | "service_policy" | "integration" | "none";
    id: string;
    name?: string;
    scope: "organization" | "workspace" | "resource" | "unverified";
    permission?: string;
    status: "active" | "revoked" | "stale" | "not_found" | "unverified";
  }>;
  inheritedFrom?: {
    type: "role" | "organization" | "workspace" | "group" | "policy" | "capability" | "direct";
    id: string;
    name?: string;
  };
  evaluatedAt: string;
};

export type ZoriEffectiveAccessSummary = {
  decisionId: string;
  subject: ZoriSubjectRef;
  subjectStatus: "active" | "revoked" | "not_found" | "unverified";
  scope: ZoriAccessScope;
  roles: Array<{
    id: string;
    name: string;
    slug?: string;
    scope: "organization" | "workspace";
  }>;
  permissions: string[];
  capabilities?: Array<{
    id: string;
    key: string;
    enabled: boolean;
  }>;
  directGrantsCount: number;
  inheritedGrantsCount: number;
  sources: ZoriAccessDecision["sources"];
  deniedSources: ZoriAccessDecision["sources"];
  coverage?: Array<{
    subject: ZoriSubjectRef;
    subjectStatus: "active" | "revoked" | "not_found" | "unverified";
    allowed: boolean;
    permissions: string[];
  }>;
  policyVersion: string;
  evaluatedAt: string;
};

/** Server roles may carry the `*` wildcard (e.g. super_admin) — it satisfies
 * every requirement, matching the server's authorization semantics. */
const WILDCARD = "*";

function ownedSet(
  effective: readonly string[] | ReadonlySet<string>,
): ReadonlySet<string> {
  return effective instanceof Set ? effective : new Set(effective);
}

/** True when every required capability is present in the effective set. */
export function hasCapability(
  effective: readonly string[] | ReadonlySet<string>,
  required: string,
): boolean {
  const owned = ownedSet(effective);
  return owned.has(WILDCARD) || owned.has(required);
}

/** Required capabilities absent from the effective set. */
export function missingCapabilities(
  effective: readonly string[] | ReadonlySet<string>,
  required: readonly string[],
): string[] {
  const owned = ownedSet(effective);
  if (owned.has(WILDCARD)) return [];
  return required.filter((capability) => !owned.has(capability));
}

/** Explain why a capability requirement passes or fails, for UI presentation. */
export function explainCapability(
  effective: readonly string[] | ReadonlySet<string>,
  required: readonly string[],
): ZoriCapabilityExplanation {
  const missing = missingCapabilities(effective, required);
  return { allowed: missing.length === 0, required, missing };
}

/** Create a canonical subject reference for authorization checks. */
export function createSubjectRef(
  type: ZoriSubjectType,
  id: string,
  organizationId: string,
  options?: { name?: string; workspaceId?: string | null },
): ZoriSubjectRef {
  return {
    type,
    id,
    organizationId,
    workspaceId: options?.workspaceId ?? null,
    ...(options?.name ? { name: options.name } : {}),
  };
}

/** Determine if an access decision allows the action. */
export function canPerformAction(
  decision: ZoriAccessDecision | null | undefined,
): boolean {
  return decision?.allowed === true;
}

/** Render a human-readable explanation of an access decision. */
export function explainAccessDecision(decision: ZoriAccessDecision): string {
  if (decision.allowed) {
    if (decision.inheritedFrom?.name) {
      return `Allowed: granted via role '${decision.inheritedFrom.name}' (${decision.inheritedFrom.type} scope)`;
    }
    return `Allowed: ${decision.reason}`;
  }
  return `Denied: ${decision.reason}`;
}
